import { useState, useMemo } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

// ══════════════════════════════════════════════════════════════
// DATA
// ══════════════════════════════════════════════════════════════

const MONTHLY_REVENUE = [
  { month: "Jan", revenue: 42000, orders: 310, returns: 18, profit: 14700, expenses: 27300 },
  { month: "Feb", revenue: 38500, orders: 278, returns: 22, profit: 12705, expenses: 25795 },
  { month: "Mar", revenue: 51000, orders: 412, returns: 14, profit: 19890, expenses: 31110 },
  { month: "Apr", revenue: 47200, orders: 365, returns: 19, profit: 17464, expenses: 29736 },
  { month: "May", revenue: 53800, orders: 430, returns: 11, profit: 21520, expenses: 32280 },
  { month: "Jun", revenue: 61000, orders: 498, returns: 16, profit: 25620, expenses: 35380 },
  { month: "Jul", revenue: 58400, orders: 461, returns: 20, profit: 23360, expenses: 35040 },
  { month: "Aug", revenue: 64200, orders: 510, returns: 13, profit: 27606, expenses: 36594 },
  { month: "Sep", revenue: 59700, orders: 477, returns: 17, profit: 24477, expenses: 35223 },
  { month: "Oct", revenue: 72100, orders: 562, returns: 21, profit: 31724, expenses: 40376 },
  { month: "Nov", revenue: 89300, orders: 701, returns: 28, profit: 41678, expenses: 47622 },
  { month: "Dec", revenue: 103000, orders: 820, returns: 35, profit: 51500, expenses: 51500 },
];

const WEEKLY_REVENUE = [
  { day: "Mon", revenue: 8200, orders: 64 },
  { day: "Tue", revenue: 9100, orders: 71 },
  { day: "Wed", revenue: 7800, orders: 58 },
  { day: "Thu", revenue: 11200, orders: 89 },
  { day: "Fri", revenue: 13500, orders: 107 },
  { day: "Sat", revenue: 15800, orders: 124 },
  { day: "Sun", revenue: 12400, orders: 98 },
];

const HOURLY_ORDERS = [
  { hour: "00", orders: 12 }, { hour: "02", orders: 8 }, { hour: "04", orders: 5 },
  { hour: "06", orders: 18 }, { hour: "08", orders: 42 }, { hour: "10", orders: 78 },
  { hour: "12", orders: 95 }, { hour: "14", orders: 88 }, { hour: "16", orders: 102 },
  { hour: "18", orders: 115 }, { hour: "20", orders: 98 }, { hour: "22", orders: 54 },
];

const TRAFFIC_DATA = [
  { month: "Jan", sessions: 12400, pageviews: 38200, bounce: 42, newUsers: 7200 },
  { month: "Feb", sessions: 10800, pageviews: 33100, bounce: 45, newUsers: 6100 },
  { month: "Mar", sessions: 15600, pageviews: 47800, bounce: 38, newUsers: 9400 },
  { month: "Apr", sessions: 14200, pageviews: 43500, bounce: 40, newUsers: 8100 },
  { month: "May", sessions: 17800, pageviews: 54200, bounce: 36, newUsers: 10800 },
  { month: "Jun", sessions: 21000, pageviews: 63100, bounce: 33, newUsers: 13200 },
];

const ACQUISITION = [
  { source: "Organic Search", value: 38, color: "#6366f1" },
  { source: "Direct",         value: 22, color: "#8b5cf6" },
  { source: "Social Media",   value: 18, color: "#ec4899" },
  { source: "Email",          value: 12, color: "#f59e0b" },
  { source: "Paid Ads",       value: 7,  color: "#10b981" },
  { source: "Referral",       value: 3,  color: "#3b82f6" },
];

const FUNNEL_DATA = [
  { name: "Visitors",     value: 21000, fill: "#6366f1" },
  { name: "Product Views",value: 14800, fill: "#8b5cf6" },
  { name: "Add to Cart",  value: 6200,  fill: "#a855f7" },
  { name: "Checkout",     value: 3800,  fill: "#ec4899" },
  { name: "Purchased",    value: 2140,  fill: "#f43f5e" },
];

const TOP_PRODUCTS = [
  { id: 1, name: "Wireless Headphones Pro", category: "Electronics", sales: 1842, revenue: 276300, stock: 143, rating: 4.8, trend: "up" },
  { id: 2, name: "Leather Running Shoes",   category: "Footwear",    sales: 1540, revenue: 184800, stock: 89,  rating: 4.6, trend: "up" },
  { id: 3, name: "Organic Face Serum",      category: "Beauty",      sales: 1320, revenue: 118800, stock: 214, rating: 4.7, trend: "down" },
  { id: 4, name: "Smart Watch Series X",    category: "Electronics", sales: 1190, revenue: 333200, stock: 56,  rating: 4.9, trend: "up" },
  { id: 5, name: "Yoga Mat Premium",        category: "Sports",      sales: 980,  revenue: 49000,  stock: 312, rating: 4.5, trend: "stable" },
  { id: 6, name: "Coffee Maker Deluxe",     category: "Kitchen",     sales: 870,  revenue: 130500, stock: 72,  rating: 4.4, trend: "up" },
  { id: 7, name: "Minimalist Backpack",     category: "Accessories", sales: 760,  revenue: 91200,  stock: 130, rating: 4.6, trend: "down" },
];

const RECENT_ORDERS = [
  { id: "#ORD-8821", customer: "Priya Sharma",   product: "Smart Watch Series X",    date: "22 Mar 2026", amount: 279, status: "Delivered" },
  { id: "#ORD-8820", customer: "Arjun Mehta",    product: "Wireless Headphones Pro", date: "22 Mar 2026", amount: 150, status: "Processing" },
  { id: "#ORD-8819", customer: "Sarah Johnson",  product: "Leather Running Shoes",   date: "21 Mar 2026", amount: 120, status: "Shipped" },
  { id: "#ORD-8818", customer: "Li Wei",          product: "Organic Face Serum",      date: "21 Mar 2026", amount: 90,  status: "Delivered" },
  { id: "#ORD-8817", customer: "Carlos Rivera",  product: "Coffee Maker Deluxe",     date: "20 Mar 2026", amount: 150, status: "Cancelled" },
  { id: "#ORD-8816", customer: "Emma Wilson",    product: "Yoga Mat Premium",        date: "20 Mar 2026", amount: 50,  status: "Delivered" },
  { id: "#ORD-8815", customer: "Rohan Das",      product: "Minimalist Backpack",     date: "19 Mar 2026", amount: 120, status: "Processing" },
];

const COUPONS = [
  { code: "SAVE20",    type: "Percentage", value: "20%",  uses: 1240, limit: 2000, expiry: "31 Mar 2026", status: "Active" },
  { code: "FLAT50",    type: "Flat",       value: "$50",  uses: 830,  limit: 1000, expiry: "15 Apr 2026", status: "Active" },
  { code: "WELCOME10", type: "Percentage", value: "10%",  uses: 3100, limit: 5000, expiry: "30 Jun 2026", status: "Active" },
  { code: "FLASH30",   type: "Percentage", value: "30%",  uses: 500,  limit: 500,  expiry: "10 Mar 2026", status: "Expired" },
  { code: "VIP100",    type: "Flat",       value: "$100", uses: 45,   limit: 100,  expiry: "31 Dec 2026", status: "Active" },
];

const PAGES = [
  { page: "/home",                    views: 48200, sessions: 21000, avgTime: "2m 14s", bounce: "33%", conversions: 842 },
  { page: "/products",               views: 36100, sessions: 18400, avgTime: "3m 42s", bounce: "28%", conversions: 1240 },
  { page: "/product/headphones-pro", views: 14800, sessions: 12100, avgTime: "4m 18s", bounce: "22%", conversions: 510 },
  { page: "/cart",                   views: 9200,  sessions: 8900,  avgTime: "1m 55s", bounce: "41%", conversions: 620 },
  { page: "/checkout",               views: 6100,  sessions: 6000,  avgTime: "3m 02s", bounce: "18%", conversions: 580 },
  { page: "/blog",                   views: 11400, sessions: 7200,  avgTime: "5m 30s", bounce: "52%", conversions: 120 },
];

const CATEGORY_SALES = [
  { name: "Electronics", value: 38 },
  { name: "Footwear",    value: 21 },
  { name: "Beauty",      value: 14 },
  { name: "Sports",      value: 11 },
  { name: "Kitchen",     value: 9  },
  { name: "Accessories", value: 7  },
];

const CUSTOMER_SEGMENTS = [
  { segment: "VIP",     count: 420,  revenue: 184000, avg: 438, color: "#6366f1" },
  { segment: "Loyal",   count: 1280, revenue: 243200, avg: 190, color: "#8b5cf6" },
  { segment: "Regular", count: 2840, revenue: 312400, avg: 110, color: "#ec4899" },
  { segment: "New",     count: 1300, revenue: 91000,  avg: 70,  color: "#f59e0b" },
];

const GEO_DATA = [
  { country: "India",          orders: 1842, revenue: 184200, flag: "🇮🇳" },
  { country: "United States",  orders: 1540, revenue: 308000, flag: "🇺🇸" },
  { country: "United Kingdom", orders: 980,  revenue: 147000, flag: "🇬🇧" },
  { country: "Germany",        orders: 720,  revenue: 108000, flag: "🇩🇪" },
  { country: "Australia",      orders: 540,  revenue: 81000,  flag: "🇦🇺" },
  { country: "Canada",         orders: 460,  revenue: 69000,  flag: "🇨🇦" },
];

const PAYMENT_METHODS = [
  { name: "Credit Card", value: 44, color: "#6366f1" },
  { name: "UPI / GPay",  value: 28, color: "#10b981" },
  { name: "PayPal",      value: 14, color: "#f59e0b" },
  { name: "Net Banking", value: 9,  color: "#3b82f6" },
  { name: "COD",         value: 5,  color: "#f43f5e" },
];

const ALERTS = [
  { type: "warning", msg: "Smart Watch Series X — Stock critically low (56 units)", time: "2h ago" },
  { type: "error",   msg: "Payment gateway timeout — 3 orders pending retry",       time: "4h ago" },
  { type: "info",    msg: "FLASH30 coupon has reached its usage limit",              time: "6h ago" },
  { type: "success", msg: "Revenue target for March exceeded by 8.2%",              time: "1d ago" },
];

// ── CUSTOMERS DATA ───────────────────────────────────────────
const CUSTOMERS = [
  { id: "CUS-001", name: "Priya Sharma",   email: "priya@email.com",   country: "India",  orders: 14, totalSpent: 3892, lastOrder: "22 Mar 2026", segment: "VIP",     status: "Active" },
  { id: "CUS-002", name: "Arjun Mehta",    email: "arjun@email.com",   country: "India",  orders: 9,  totalSpent: 1340, lastOrder: "22 Mar 2026", segment: "Loyal",   status: "Active" },
  { id: "CUS-003", name: "Sarah Johnson",  email: "sarah@email.com",   country: "USA",    orders: 22, totalSpent: 5610, lastOrder: "21 Mar 2026", segment: "VIP",     status: "Active" },
  { id: "CUS-004", name: "Li Wei",         email: "liwei@email.com",   country: "China",  orders: 6,  totalSpent: 780,  lastOrder: "21 Mar 2026", segment: "Regular", status: "Active" },
  { id: "CUS-005", name: "Carlos Rivera",  email: "carlos@email.com",  country: "Mexico", orders: 3,  totalSpent: 420,  lastOrder: "20 Mar 2026", segment: "New",     status: "Active" },
  { id: "CUS-006", name: "Emma Wilson",    email: "emma@email.com",    country: "UK",     orders: 11, totalSpent: 2100, lastOrder: "20 Mar 2026", segment: "Loyal",   status: "Active" },
  { id: "CUS-007", name: "Rohan Das",      email: "rohan@email.com",   country: "India",  orders: 4,  totalSpent: 580,  lastOrder: "19 Mar 2026", segment: "Regular", status: "Inactive" },
  { id: "CUS-008", name: "Anna Müller",    email: "anna@email.com",    country: "Germany",orders: 18, totalSpent: 4280, lastOrder: "18 Mar 2026", segment: "VIP",     status: "Active" },
  { id: "CUS-009", name: "James Kim",      email: "james@email.com",   country: "USA",    orders: 7,  totalSpent: 930,  lastOrder: "17 Mar 2026", segment: "Regular", status: "Active" },
  { id: "CUS-010", name: "Fatima Al-Said", email: "fatima@email.com",  country: "UAE",    orders: 2,  totalSpent: 210,  lastOrder: "15 Mar 2026", segment: "New",     status: "Active" },
];

const CUSTOMER_GROWTH = [
  { month: "Jan", newCustomers: 420, returning: 890,  churned: 32 },
  { month: "Feb", newCustomers: 380, returning: 870,  churned: 28 },
  { month: "Mar", newCustomers: 510, returning: 940,  churned: 21 },
  { month: "Apr", newCustomers: 465, returning: 920,  churned: 35 },
  { month: "May", newCustomers: 540, returning: 1010, churned: 18 },
  { month: "Jun", newCustomers: 620, returning: 1120, churned: 24 },
  { month: "Jul", newCustomers: 590, returning: 1080, churned: 30 },
  { month: "Aug", newCustomers: 640, returning: 1150, churned: 22 },
  { month: "Sep", newCustomers: 610, returning: 1120, churned: 27 },
  { month: "Oct", newCustomers: 700, returning: 1230, churned: 19 },
  { month: "Nov", newCustomers: 840, returning: 1410, churned: 15 },
  { month: "Dec", newCustomers: 930, returning: 1560, churned: 42 },
];

const CLV_DATA = [
  { segment: "VIP",     clv: 4200, acqCost: 120, margin: 68 },
  { segment: "Loyal",   clv: 1800, acqCost: 85,  margin: 52 },
  { segment: "Regular", clv: 820,  acqCost: 60,  margin: 38 },
  { segment: "New",     clv: 280,  acqCost: 45,  margin: 22 },
];

const RETENTION_DATA = [
  { cohort: "Jan",  m0: 100, m1: 72, m2: 61, m3: 54, m4: 49, m5: 45 },
  { cohort: "Feb",  m0: 100, m1: 68, m2: 57, m3: 51, m4: 46, m5: 42 },
  { cohort: "Mar",  m0: 100, m1: 75, m2: 64, m3: 58, m4: 53, m5: null },
  { cohort: "Apr",  m0: 100, m1: 71, m2: 60, m3: 54, m4: null, m5: null },
  { cohort: "May",  m0: 100, m1: 74, m2: 63, m3: null, m4: null, m5: null },
  { cohort: "Jun",  m0: 100, m1: 77, m2: null, m3: null, m4: null, m5: null },
];

const SATISFACTION = [
  { month: "Jan", nps: 42, csat: 81, ces: 3.8 },
  { month: "Feb", nps: 44, csat: 82, ces: 3.7 },
  { month: "Mar", nps: 48, csat: 84, ces: 3.5 },
  { month: "Apr", nps: 46, csat: 83, ces: 3.6 },
  { month: "May", nps: 52, csat: 86, ces: 3.3 },
  { month: "Jun", nps: 58, csat: 89, ces: 3.1 },
];

const CUSTOMER_RADAR = [
  { metric: "Satisfaction",  VIP: 95, Loyal: 82, Regular: 68, New: 55 },
  { metric: "Retention",     VIP: 92, Loyal: 78, Regular: 61, New: 42 },
  { metric: "AOV",           VIP: 88, Loyal: 72, Regular: 55, New: 38 },
  { metric: "Frequency",     VIP: 90, Loyal: 75, Regular: 50, New: 30 },
  { metric: "Referrals",     VIP: 85, Loyal: 60, Regular: 35, New: 18 },
  { metric: "Engagement",    VIP: 93, Loyal: 80, Regular: 64, New: 48 },
];

// ── CATEGORIES DATA ──────────────────────────────────────────
const CATEGORIES = [
  { id: 1, name: "Electronics",  products: 184, revenue: 609500, orders: 3032, avgPrice: 210, growth: "+24.2%", rating: 4.75, color: "#6366f1", trend: "up" },
  { id: 2, name: "Footwear",     products: 212, revenue: 369600, orders: 2520, avgPrice: 110, growth: "+11.4%", rating: 4.58, color: "#8b5cf6", trend: "up" },
  { id: 3, name: "Beauty",       products: 310, revenue: 237600, orders: 2640, avgPrice: 72,  growth: "+8.1%",  rating: 4.62, color: "#ec4899", trend: "up" },
  { id: 4, name: "Sports",       products: 145, revenue: 196000, orders: 1960, avgPrice: 78,  growth: "-2.3%",  rating: 4.48, color: "#f59e0b", trend: "down" },
  { id: 5, name: "Kitchen",      products: 98,  revenue: 261000, orders: 1740, avgPrice: 145, growth: "+15.8%", rating: 4.41, color: "#10b981", trend: "up" },
  { id: 6, name: "Accessories",  products: 335, revenue: 182400, orders: 1520, avgPrice: 88,  growth: "+5.7%",  rating: 4.53, color: "#3b82f6", trend: "stable" },
];

const CATEGORY_MONTHLY = [
  { month: "Jan", Electronics: 38000, Footwear: 22000, Beauty: 14000, Sports: 9000, Kitchen: 12000, Accessories: 8000 },
  { month: "Feb", Electronics: 32000, Footwear: 19000, Beauty: 12000, Sports: 8500, Kitchen: 11000, Accessories: 7500 },
  { month: "Mar", Electronics: 45000, Footwear: 26000, Beauty: 16000, Sports: 10000,Kitchen: 14000, Accessories: 9000 },
  { month: "Apr", Electronics: 41000, Footwear: 24000, Beauty: 15000, Sports: 9500, Kitchen: 13000, Accessories: 8500 },
  { month: "May", Electronics: 48000, Footwear: 28000, Beauty: 17000, Sports: 10500,Kitchen: 15000, Accessories: 9500 },
  { month: "Jun", Electronics: 55000, Footwear: 32000, Beauty: 20000, Sports: 11000,Kitchen: 18000, Accessories: 11000 },
];

const CATEGORY_RADAR = [
  { metric: "Revenue",   Electronics: 95, Footwear: 72, Beauty: 55, Sports: 42, Kitchen: 58, Accessories: 40 },
  { metric: "Orders",    Electronics: 88, Footwear: 76, Beauty: 68, Sports: 54, Kitchen: 52, Accessories: 48 },
  { metric: "Growth",    Electronics: 92, Footwear: 65, Beauty: 60, Sports: 35, Kitchen: 78, Accessories: 50 },
  { metric: "Rating",    Electronics: 90, Footwear: 82, Beauty: 85, Sports: 78, Kitchen: 75, Accessories: 80 },
  { metric: "Margin",    Electronics: 78, Footwear: 62, Beauty: 70, Sports: 58, Kitchen: 65, Accessories: 55 },
  { metric: "StockHealth",Electronics: 80, Footwear: 70, Beauty: 88, Sports: 92, Kitchen: 74, Accessories: 85 },
];

const CAT_GROWTH_TREND = [
  { month: "Jan", Electronics: 18, Footwear: 8,  Beauty: 5,  Sports: -1, Kitchen: 12, Accessories: 4 },
  { month: "Feb", Electronics: 20, Footwear: 9,  Beauty: 6,  Sports: -2, Kitchen: 13, Accessories: 5 },
  { month: "Mar", Electronics: 22, Footwear: 10, Beauty: 7,  Sports: -1, Kitchen: 15, Accessories: 5 },
  { month: "Apr", Electronics: 23, Footwear: 11, Beauty: 7,  Sports: -3, Kitchen: 16, Accessories: 6 },
  { month: "May", Electronics: 24, Footwear: 11, Beauty: 8,  Sports: -2, Kitchen: 16, Accessories: 6 },
  { month: "Jun", Electronics: 24, Footwear: 11, Beauty: 8,  Sports: -2, Kitchen: 16, Accessories: 6 },
];

const PIE_COLORS = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6"];

const alertStyles = {
  warning: { bg: "bg-amber-50 border-amber-200",   txt: "text-amber-700",   icon: "⚠️" },
  error:   { bg: "bg-red-50 border-red-200",       txt: "text-red-700",     icon: "🔴" },
  info:    { bg: "bg-blue-50 border-blue-200",     txt: "text-blue-700",    icon: "ℹ️" },
  success: { bg: "bg-emerald-50 border-emerald-200",txt: "text-emerald-700",icon: "✅" },
};

// ══════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ══════════════════════════════════════════════════════════════

const fmtNum = n => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;

const StatusBadge = ({ status }) => {
  const map = {
    Delivered: "bg-emerald-100 text-emerald-700", Processing: "bg-blue-100 text-blue-700",
    Shipped: "bg-indigo-100 text-indigo-700",     Cancelled:  "bg-red-100 text-red-700",
    Active:  "bg-emerald-100 text-emerald-700",   Expired:    "bg-red-100 text-red-700",
    Inactive:"bg-gray-100 text-gray-500",
    VIP:     "bg-indigo-100 text-indigo-700",     Loyal:      "bg-violet-100 text-violet-700",
    Regular: "bg-pink-100 text-pink-700",         New:        "bg-amber-100 text-amber-700",
  };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${map[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
};

const TrendIcon = ({ trend }) => {
  if (trend === "up")   return <span className="text-emerald-500 text-sm font-bold">↑</span>;
  if (trend === "down") return <span className="text-red-500 text-sm font-bold">↓</span>;
  return <span className="text-gray-400 text-sm font-bold">→</span>;
};

const MiniStat = ({ label, value, delta, positive }) => (
  <div className="text-center px-2">
    <p className="text-xs text-gray-400 font-medium">{label}</p>
    <p className="text-lg font-bold text-gray-800">{value}</p>
    {delta && <p className={`text-xs font-semibold ${positive ? "text-emerald-500" : "text-red-500"}`}>{delta}</p>}
  </div>
);

const StatCard = ({ icon, label, value, sub, color, positive }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 ${color}`}>{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
      {sub && <p className={`text-xs mt-0.5 font-semibold ${positive === true ? "text-emerald-500" : positive === false ? "text-red-500" : "text-gray-400"}`}>{sub}</p>}
    </div>
  </div>
);

const SectionCard = ({ title, children, action, subtitle }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-start justify-between px-6 py-4 border-b border-gray-50">
      <div>
        <h2 className="text-base font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p className="font-semibold mb-1 text-gray-300">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#fff" }}>
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const Gauge = ({ value, max, label, color }) => {
  const pct = Math.round((value / max) * 100);
  const data = [{ value: pct }, { value: 100 - pct }];
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-14 overflow-hidden">
        <div className="absolute inset-0 scale-y-[2] origin-bottom">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="100%" startAngle={180} endAngle={0}
                innerRadius={28} outerRadius={44} dataKey="value" strokeWidth={0}>
                <Cell fill={color} /><Cell fill="#f3f4f6" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center">
          <span className="text-base font-bold text-gray-800">{pct}%</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 font-medium text-center mt-1">{label}</p>
    </div>
  );
};

// ── Retention heat-cell ──────────────────────────────────────
const heatColor = (val) => {
  if (val === null) return "bg-gray-50 text-gray-300";
  if (val >= 90) return "bg-indigo-600 text-white";
  if (val >= 75) return "bg-indigo-400 text-white";
  if (val >= 60) return "bg-indigo-200 text-indigo-900";
  if (val >= 45) return "bg-indigo-100 text-indigo-700";
  return "bg-gray-100 text-gray-500";
};

// ══════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ══════════════════════════════════════════════════════════════
export default function Coupons() {
  const [activeTab,    setActiveTab]    = useState("coupons");
  const [orderSearch,  setOrderSearch]  = useState("");
  const [orderStatus,  setOrderStatus]  = useState("All");
  const [productSearch,setProductSearch]= useState("");
  const [couponSearch, setCouponSearch] = useState("");
  const [customerSearch,setCustomerSearch]=useState("");
  const [customerSeg,  setCustomerSeg]  = useState("All");
  const [catSearch,    setCatSearch]    = useState("");
  const [revenueRange, setRevenueRange] = useState("12m");
  const [sidebarOpen,  setSidebarOpen]  = useState(true);

  const tabs = [
    { id: "overview",   label: "Overview",   icon: "◈" },
    { id: "products",   label: "Products",   icon: "⬡" },
    { id: "orders",     label: "Orders",     icon: "📦" },
    { id: "customers",  label: "Customers",  icon: "👥" },
    { id: "categories", label: "Categories", icon: "🗂️" },
    { id: "pages",      label: "Pages",      icon: "📄" },
    { id: "coupons",    label: "Coupons",    icon: "🏷️" },
  ];

  const revenueData = useMemo(() => {
    if (revenueRange === "6m") return MONTHLY_REVENUE.slice(6);
    if (revenueRange === "3m") return MONTHLY_REVENUE.slice(9);
    return MONTHLY_REVENUE;
  }, [revenueRange]);

  const filteredOrders = useMemo(() =>
    RECENT_ORDERS.filter(o =>
      (orderStatus === "All" || o.status === orderStatus) &&
      (o.id.toLowerCase().includes(orderSearch.toLowerCase()) ||
       o.customer.toLowerCase().includes(orderSearch.toLowerCase()) ||
       o.product.toLowerCase().includes(orderSearch.toLowerCase()))
    ), [orderSearch, orderStatus]);

  const filteredProducts = useMemo(() =>
    TOP_PRODUCTS.filter(p =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.category.toLowerCase().includes(productSearch.toLowerCase())
    ), [productSearch]);

  const filteredCoupons = useMemo(() =>
    COUPONS.filter(c => c.code.toLowerCase().includes(couponSearch.toLowerCase()))
  , [couponSearch]);

  const filteredCustomers = useMemo(() =>
    CUSTOMERS.filter(c =>
      (customerSeg === "All" || c.segment === customerSeg) &&
      (c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
       c.email.toLowerCase().includes(customerSearch.toLowerCase()) ||
       c.country.toLowerCase().includes(customerSearch.toLowerCase()))
    ), [customerSearch, customerSeg]);

  const filteredCats = useMemo(() =>
    CATEGORIES.filter(c => c.name.toLowerCase().includes(catSearch.toLowerCase()))
  , [catSearch]);

  const totalRevenue  = MONTHLY_REVENUE.reduce((s, m) => s + m.revenue, 0);
  const totalOrders   = MONTHLY_REVENUE.reduce((s, m) => s + m.orders, 0);
  const totalProfit   = MONTHLY_REVENUE.reduce((s, m) => s + m.profit, 0);
  const maxGeoRevenue = Math.max(...GEO_DATA.map(g => g.revenue));

  return (
    <div className="flex flex-col overflow-y-auto  gap-2 ">


          {/* ══════════════════════════════════════════════════════
              COUPONS
          ══════════════════════════════════════════════════════ */}
          {activeTab === "coupons" && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon="🏷️" label="Total Coupons"  value="24"        sub="5 active right now" color="bg-indigo-500"/>
                <StatCard icon="✓"  label="Total Uses"     value="5,715"     sub="This year"          color="bg-emerald-500"/>
                <StatCard icon="$"  label="Discount Given" value="$84.2k"    sub="Revenue impact"     color="bg-rose-500"/>
                <StatCard icon="⚡" label="Best Coupon"    value="WELCOME10" sub="3,100 uses"         color="bg-amber-500"/>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SectionCard title="Coupon Usage">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={COUPONS}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                      <XAxis dataKey="code" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}}/>
                      <Tooltip/><Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                      <Bar dataKey="uses"  fill="#6366f1" radius={[4,4,0,0]} name="Uses"/>
                      <Bar dataKey="limit" fill="#e0e7ff" radius={[4,4,0,0]} name="Limit"/>
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
                <SectionCard title="Usage Rate by Coupon">
                  <div className="space-y-4 pt-2">
                    {COUPONS.map(c=>(
                      <div key={c.code}>
                        <div className="flex justify-between items-center mb-1.5">
                          <span className="font-mono text-sm font-bold text-gray-700">{c.code}</span>
                          <span className="text-xs text-gray-400">{c.uses} / {c.limit}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${c.uses>=c.limit?"bg-red-400":"bg-indigo-500"}`}
                            style={{width:`${Math.min((c.uses/c.limit)*100,100)}%`}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
              <SectionCard title="Coupon Management"
                action={<button className="bg-indigo-600 text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">+ Add Coupon</button>}>
                <div className="mb-4">
                  <input value={couponSearch} onChange={e=>setCouponSearch(e.target.value)} placeholder="Search coupons..."
                    className="border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-indigo-400 w-full max-w-xs"/>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                      <th className="pb-3 text-left font-semibold">Code</th><th className="pb-3 text-left font-semibold">Type</th>
                      <th className="pb-3 text-left font-semibold">Value</th><th className="pb-3 text-right font-semibold">Uses</th>
                      <th className="pb-3 text-right font-semibold">Limit</th><th className="pb-3 text-left font-semibold">Expiry</th>
                      <th className="pb-3 text-center font-semibold">Status</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredCoupons.map(c=>(
                        <tr key={c.code} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 font-mono font-bold text-indigo-600">{c.code}</td>
                          <td className="py-3 text-gray-600">{c.type}</td>
                          <td className="py-3 font-bold text-gray-800">{c.value}</td>
                          <td className="py-3 text-right text-gray-700">{c.uses.toLocaleString()}</td>
                          <td className="py-3 text-right text-gray-400">{c.limit.toLocaleString()}</td>
                          <td className="py-3 text-gray-500 text-xs">{c.expiry}</td>
                          <td className="py-3 text-center"><StatusBadge status={c.status}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </>
          )}

        </div>
  );
}